import React, { useId } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Plus, Target, Trash2 } from 'lucide-react';
import {
  CarePlanGoal, CarePlanGoalDiscipline, GOAL_DOMAINS, GOAL_STATUS_LABELS, GoalStatus, emptyGoal,
} from './carePlanGoals';

interface GoalsEditorProps {
  /** A hand-built plan's discipline (its goal domains); a template plan passes `domains` */
  discipline: CarePlanGoalDiscipline | string;
  /** Phase 20B: goal domains of a template care plan */
  domains?: readonly string[];
  goals: CarePlanGoal[];
  onChange: (goals: CarePlanGoal[]) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}

const input = (readOnly: boolean, invalid = false) =>
  `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${invalid ? 'border-red-500' : 'border-gray-300'} ${readOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`;

/**
 * Phase 19 F12: the single place a care plan's goals are written, for every discipline.
 * Each goal: domain, goal, short/long term, baseline, measurable outcome, target date, status and
 * progress note (required once a goal is no longer active).
 */
const GoalsEditor: React.FC<GoalsEditorProps> = ({ discipline, domains, goals, onChange, errors = {}, readOnly = false, collapsed = false, onToggle }) => {
  const baseId = useId();
  const update = (goalId: string, patch: Partial<CarePlanGoal>) => {
    onChange(goals.map(g => {
      if (g.goalId !== goalId) return g;
      const next = { ...g, ...patch };
      if (patch.status && patch.status !== g.status) next.statusDate = new Date().toISOString().slice(0, 10);
      return next;
    }));
  };
  const add = () => onChange([...goals, emptyGoal()]);
  const remove = (goalId: string) => onChange(goals.filter(g => g.goalId !== goalId));

  return (
    <div className="bg-green-50 rounded-lg border border-green-200">
      <button type="button" className="w-full p-4 flex justify-between items-center text-left" onClick={onToggle} aria-expanded={!collapsed}>
        <span className="flex items-center gap-2 text-md font-medium text-green-900">
          <Target size={18} aria-hidden="true" /> Goals ({goals.filter(g => g.text.trim()).length})
        </span>
        {collapsed ? <ChevronDown size={20} className="text-green-700" aria-hidden="true" /> : <ChevronUp size={20} className="text-green-700" aria-hidden="true" />}
      </button>
      {!collapsed && (
        <div className="p-4 border-t border-green-200 space-y-4">
          <p className="text-sm text-gray-600">
            Each goal needs a measurable outcome and a target date. Record the status at each revision or recertification;
            these goals and their progress fill the CMS-485 and the F00120 nursing care plan summary.
          </p>
          {errors.goals && (
            <p className="text-red-600 text-sm flex items-center" role="alert"><AlertCircle size={16} className="mr-1" aria-hidden="true" />{errors.goals}</p>
          )}
          {goals.map((g, i) => {
            const id = (field: string) => `${baseId}-${i}-${field}`;
            const goalError = errors[`goal-${g.goalId}`];
            return (
              <fieldset key={g.goalId} className="bg-white rounded-md border border-gray-200 p-3">
                <legend className="px-1 text-sm font-medium text-gray-800">Goal {i + 1}</legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label htmlFor={id('domain')} className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                    <select id={id('domain')} disabled={readOnly} value={g.domain} onChange={e => update(g.goalId, { domain: e.target.value })} className={input(readOnly)}>
                      <option value="">Select domain</option>
                      {[...new Set([...(domains || GOAL_DOMAINS[discipline as CarePlanGoalDiscipline] || ['Other']), ...(g.domain ? [g.domain] : [])])].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={id('term')} className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                    <select id={id('term')} disabled={readOnly} value={g.term || ''} onChange={e => update(g.goalId, { term: (e.target.value || null) as CarePlanGoal['term'] })} className={input(readOnly)}>
                      <option value="">Select term</option>
                      <option value="SHORT">Short-term</option>
                      <option value="LONG">Long-term</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor={id('target')} className="block text-sm font-medium text-gray-700 mb-1">Target date *</label>
                    <input id={id('target')} type="date" disabled={readOnly} value={g.targetDate} onChange={e => update(g.goalId, { targetDate: e.target.value })} className={input(readOnly)} />
                  </div>
                  <div className="md:col-span-3">
                    <label htmlFor={id('text')} className="block text-sm font-medium text-gray-700 mb-1">Goal *</label>
                    <textarea id={id('text')} rows={2} disabled={readOnly} value={g.text} onChange={e => update(g.goalId, { text: e.target.value })} className={input(readOnly)}
                      placeholder="e.g., Caregiver will perform an emergency trach change independently" />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor={id('measure')} className="block text-sm font-medium text-gray-700 mb-1">Measurable outcome *</label>
                    <input id={id('measure')} type="text" disabled={readOnly} value={g.measure} onChange={e => update(g.goalId, { measure: e.target.value })} className={input(readOnly)}
                      placeholder="e.g., 3 of 3 return demonstrations" />
                  </div>
                  <div>
                    <label htmlFor={id('baseline')} className="block text-sm font-medium text-gray-700 mb-1">Baseline</label>
                    <input id={id('baseline')} type="text" disabled={readOnly} value={g.baseline} onChange={e => update(g.goalId, { baseline: e.target.value })} className={input(readOnly)} />
                  </div>
                  <div>
                    <label htmlFor={id('status')} className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select id={id('status')} disabled={readOnly} value={g.status} onChange={e => update(g.goalId, { status: e.target.value as GoalStatus })} className={input(readOnly)}>
                      {(Object.keys(GOAL_STATUS_LABELS) as GoalStatus[]).map(s => <option key={s} value={s}>{GOAL_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor={id('progress')} className="block text-sm font-medium text-gray-700 mb-1">Progress note{g.status !== 'ACTIVE' ? ' *' : ''}</label>
                    <input id={id('progress')} type="text" disabled={readOnly} value={g.progressNote} onChange={e => update(g.goalId, { progressNote: e.target.value })}
                      className={input(readOnly, Boolean(goalError))} aria-invalid={Boolean(goalError)} placeholder="Progress since the last review" />
                    {goalError && <p className="text-red-600 text-sm mt-1">{goalError}</p>}
                  </div>
                </div>
                {!readOnly && (
                  <button type="button" onClick={() => remove(g.goalId)} className="mt-2 inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-800">
                    <Trash2 size={14} aria-hidden="true" /> Remove goal
                  </button>
                )}
              </fieldset>
            );
          })}
          {!readOnly && (
            <button type="button" onClick={add} className="inline-flex items-center gap-1 px-3 py-2 min-h-[44px] text-sm font-medium text-green-800 border border-green-300 rounded-md hover:bg-green-100">
              <Plus size={16} aria-hidden="true" /> Add goal
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GoalsEditor;
